


































import { Vue, Component, Prop } from "vue-property-decorator";
import { IresDeviceItemData } from "@/data/models/IresDeviceItemData";
import { ProgramChain } from "@/data/models/ProgramChain";
import { TimeRange } from "@/data/models/TimeRange";
import { getAdjustedEndDate } from "@/data/models/RunOverview";
import { formatDurationLong, formatDateShort, timestampToDate } from "@/utils/DateFormatter";
import { observeMostRecentProgramChainBelongsToClient } from "@/data/repository/ProgramChainRepository";
import {
  calculateIresDeviceSortData,
  getLastProjectInfoOfIresDevice,
  getProjectInfoOfIresDevice,
} from "@/data/models/DeviceSortOrder";
import Panel from "@/data/models/Panel";
import { firestore } from "firebase/app";

import router from "@/router/router";

import IresDeviceOverviewItemComponent from "@/components/devices/IresDeviceOverviewItem.vue";
import { getClientId, getMemberClientIds } from "@/data/repository/ClientRepository";
import {
  getIresDeviceCategory,
  IresDeviceCategory,
  isActiveProgramChainOfIresDevice,
} from "@/data/models/IresDevice";
import {
  ComBox,
  ComBoxDeviceState,
  getComBoxState,
} from "@/data/models/ComBox";

import {
  DeviceFilter,
  matchesIresDeviceFilter,
} from "@/data/models/DeviceFilter";
import { IresDeviceAssignment } from "@/data/models/IresDeviceAssignment";
import {
  getCachedAssignmentsOfCurrentClient,
  getCachedAssignmentsOfMemberClients,
} from "@/data/repository/IresDeviceAssignmentRepository";
import Client from "@/data/models/Client";
import { viewHyDryDeviceDetailsOfMemberAccess } from "@/permissions/UserPermissions";
import { viewProgramChainsOfAllClientsClientAccess } from "@/permissions/ClientPermissions";
import { setLastVisitedItem, StorageKeyLastVisitedItemLevel } from "@/utils/Utils";
import { getEffectiveUser } from "@/utils/ViewAs";

@Component({
  components: {
    "ires-device-overview-item": IresDeviceOverviewItemComponent,
  },
})
export default class IresDeviceItem extends Vue {
  @Prop() iresDeviceItem!: IresDeviceItemData;

  @Prop({ default: "" })
  backgroundStyle!: string;

  @Prop({ default: false }) isSelectionMode!: boolean;
  @Prop({ default: false }) isSelected!: boolean;

  @Prop({ default: () => new Map<string, Client>() })
  memberClients!: Map<string, Client>;

  @Prop({ default: false })
  isAllowedToMoveDevicesToAnotherClient!: boolean;

  subscriptions: Array<() => void> = [];
  programChains: Array<ProgramChain> = [];
  iresDeviceAssignments: IresDeviceAssignment[] = [];
  programChainDataReady = false;

  clientId = getClientId();
  // Whose view the row shows: the chosen user while "Sicht auf" is active (ticket 2610052).
  activeUser = getEffectiveUser();

  componentVisible = false;
  isObservingProgramChainData = false;

  // Rows are destroyed while scrolling, so an assignment read can outlive its row.
  isDestroyed = false;

  isInfoOfLastProject = false;

  get isAllowedToViewIresDeviceDetailsOfMemberClients() {
    const activeUser = getEffectiveUser();

    return viewHyDryDeviceDetailsOfMemberAccess(activeUser);
  }

  get memberClientIds() {
    return getMemberClientIds();
  }

  /**
   * Whether this device counts as in usage.
   *
   * `null` is deliberate and must stay: `allHyDryDevicesAccess(null)` returns **true**, so
   * passing no user skips `matchesIresDeviceFilter`'s responsible-users gate and asks the
   * plain question "is this device in use", independent of who is looking. Passing the
   * real user instead makes the answer depend on whether that user is responsible for the
   * device's project, which is not what any of the consumers below want: the "in usage"
   * comment, the last-project fallback in `projectInfo`, the last-update text, row
   * selectability, and whether a click opens the device detail or the drying history.
   */
  get isInUsage() {
    return matchesIresDeviceFilter(
      DeviceFilter.IN_USAGE,
      this.iresDeviceItem.iresDevice,
      null
    );
  }

  get projectInfo() {
    if (!this.programChainDataReady) {
      return null;
    }

    this.isInfoOfLastProject = false;
    const info = getProjectInfoOfIresDevice(
      this.iresDeviceItem.iresDevice,
      this.iresDeviceItem.matchingClientPanel ?? null
    );

    if (
      !info &&
      this.isInUsage &&
      !isActiveProgramChainOfIresDevice(
        this.iresDevice,
        this.selectedProgramChain
      ) &&
      this.iresDeviceItem.matchingClientPanel
    ) {
      const lastProjectInfo = getLastProjectInfoOfIresDevice(
        this.iresDeviceItem.matchingClientPanel
      );

      // Only surface the last project when the run currently on display is the
      // one that belonged to it (device "still on after being released from the
      // project"). If the most recent run is a separate, later run that does
      // not overlap the last project's assignment window, pairing that project
      // with this run's dates/graph is misleading, so the fallback is dropped.
      if (
        lastProjectInfo &&
        this.lastProjectOverlapsDisplayedChain(
          lastProjectInfo.currentProjectAssignmentTimeRange
        )
      ) {
        this.isInfoOfLastProject = true;
        return lastProjectInfo;
      }
    }

    return info;
  }

  /**
   * Whether the last project's assignment window overlaps the run window of the
   * program chain currently shown in this row. Gates the last-project fallback
   * in `projectInfo` so a stale project is not shown next to an unrelated,
   * later run's dates and mini graph.
   */
  private lastProjectOverlapsDisplayedChain(
    lastProjectTimeRange: TimeRange | null | undefined
  ): boolean {
    const programChain = this.selectedProgramChain;

    if (
      !lastProjectTimeRange?.startDate ||
      !programChain ||
      programChain.runs.length === 0
    ) {
      return false;
    }

    const chainStartMillis = timestampToDate(
      programChain.runs[0].startDate
    )?.getTime();
    const chainEndMillis = getAdjustedEndDate(
      programChain.runs[programChain.runs.length - 1]
    ).getTime();

    if (chainStartMillis == null) {
      return false;
    }

    const projectStartMillis = lastProjectTimeRange.startDate.toMillis();
    const projectEndMillis = lastProjectTimeRange.endDate
      ? lastProjectTimeRange.endDate.toMillis()
      : Number.MAX_SAFE_INTEGER;

    // Standard closed-interval overlap test.
    return (
      projectStartMillis <= chainEndMillis &&
      chainStartMillis <= projectEndMillis
    );
  }

  /**
   * Sort data for this row's own display only ("seit ...", the run window).
   *
   * Deliberately not reported back to the list. It is derived partly from the program
   * chain, which only loads once the row is visible, so feeding it into the list's sort
   * key made the order depend on how far the user had scrolled: the list reshuffled rows
   * already on screen as later rows loaded their chains.
   */
  get deviceSortData() {
    return calculateIresDeviceSortData(
      this.iresDevice,
      this.activeUser,
      this.iresDeviceItem.matchingClientPanel ?? null,
      this.selectedProgramChain
    );
  }

  get runningSince() {
    return this.deviceSortData.sortTimestamp;
  }

  get isAssignedToProject() {
    const currentAssignment = this.iresDevice.currentAssignment;

    if (!currentAssignment) {
      return false;
    }

    return currentAssignment.currentProjectId && currentAssignment.currentProjectId !== null && currentAssignment.currentProjectId !== ""
      ? true
      : false;
  }

  get isIresDeviceSelectable() {
    return !this.isAssignedToProject && !this.isInUsage;
  }

  get iresDeviceComments() {
    const currentAssignment = this.iresDevice.currentAssignment;

    return this.isAssignedToProject
      ? this.$t("common.currentProject", [currentAssignment?.currentProjectName]).toString()
      : this.isInUsage
        ? this.$t("iresDevice.deviceInUsage")
        : "";
  }

  get runningSinceString() {
    if (!this.programChainDataReady) {
      return "";
    }

    if (
      this.iresDeviceCategory === IresDeviceCategory.COMBOX &&
      getComBoxState(this.iresDevice as ComBox) === ComBoxDeviceState.IDLE
    ) {
      return "";
    }

    const runningSince = this.runningSince;

    if (
      (this.isInfoOfLastProject ||
      !this.projectInfo?.currentProjectAssignmentTimeRange?.startDate) &&
      !isActiveProgramChainOfIresDevice(
        this.iresDevice,
        this.selectedProgramChain
      )
    ) {
      if (
        this.selectedProgramChain &&
        this.selectedProgramChain.runs.length > 0
      ) {
        const startDate = this.selectedProgramChain.runs[0].startDate;
        const endDate = firestore.Timestamp.fromDate(
          getAdjustedEndDate(
            this.selectedProgramChain.runs[
              this.selectedProgramChain.runs.length - 1
            ]
          )
        );

        const startYear = timestampToDate(startDate)?.getFullYear();
        const endYear = timestampToDate(endDate)?.getFullYear();

        return this.$t("common.fromUntil", [
          formatDateShort(startDate, undefined, undefined, startYear !== endYear),
          formatDateShort(endDate, undefined, undefined, startYear !== endYear),
        ]).toString();
      }

      return "";
    }

    if (runningSince < 0) {
      return "";
    }

    return this.$t("common.since", [
      formatDurationLong(this, runningSince, true),
    ]);
  }

  get inventoryNumber() {
    return this.iresDeviceItem.matchingClientPanel?.inventoryNumber ?? "";
  }

  get selectedProgramChain() {
    return this.programChains[0];
  }

  get iresDevice() {
    return this.iresDeviceItem.iresDevice;
  }

  get iresDeviceCategory() {
    return getIresDeviceCategory(this.iresDevice);
  }

  get panel() {
    return this.iresDevice as Panel;
  }

  get comBox() {
    return this.iresDevice as ComBox;
  }

  get showDryingGraph() {
    return true;
  }

  get isAllowedToViewProgramChainsOfAllClients() {
    return viewProgramChainsOfAllClientsClientAccess();
  }

  openIresDeviceDetails() {
    if (this.isSelectionMode) {
      if (this.isIresDeviceSelectable) {       
        this.$emit("selectIresDevice"); 
      }
    }
    else {
      const serialNumber = this.iresDevice.serialNo;

      if (serialNumber) {
        setLastVisitedItem(
          serialNumber,
          StorageKeyLastVisitedItemLevel.LEVEL_1,
        );

        router.push({
          path: this.isInUsage
            ? `/main/devices/deviceDetail`
            : `/main/devices/dryingHistory`,
          query: {
            serialNumber: serialNumber,
          },
        });
      }
    }

  }

  onSelectIresDevice() {
    if (this.isSelectionMode && this.isIresDeviceSelectable) {
      this.$emit("selectIresDevice");
    }
  }

  async visibilityChanged(isVisible: boolean) {
    this.componentVisible = isVisible;

    if (!isVisible) {
      return;
    }

    if (this.isObservingProgramChainData) {
      return;
    }

    // Set before awaiting, so a second visibility event cannot start a second
    // subscription while the assignments are still loading.
    this.isObservingProgramChainData = true;

    // The assignments decide which program chain belongs to this client, so they have
    // to be loaded before subscribing. Loading them on visibility rather than on
    // creation means rows the user never scrolls to cost no query at all, and it closes
    // a race: created() is async, so a row that became visible first used to subscribe
    // with an empty assignment list and match no chain.
    this.iresDeviceAssignments = await this.loadIresDeviceAssignments();

    if (this.isDestroyed) {
      return;
    }

    this.subscriptions.push(
      observeMostRecentProgramChainBelongsToClient(
        this.iresDevice.serialNo,
        this.iresDeviceAssignments,
        (programChain: ProgramChain) => {
          this.programChains = [programChain];
          this.programChainDataReady = true;
        },
        this.isAllowedToViewProgramChainsOfAllClients
      )
    );
  }

  loadIresDeviceAssignments() {
    return this.isAllowedToViewIresDeviceDetailsOfMemberClients
      ? getCachedAssignmentsOfMemberClients(this.iresDeviceItem.serialNumber)
      : getCachedAssignmentsOfCurrentClient(
          this.iresDeviceItem.serialNumber,
          getClientId()
        );
  }

  beforeDestroy() {
    this.isDestroyed = true;

    this.subscriptions.forEach((subscription) => {
      subscription();
    });
  }
}
